'use client';

import UploadFile from '@/components/translate/upload-file';
import Particles from '@/components/Particles';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { motion } from 'motion/react';
import { ArrowRight, CheckCircle, Upload, Users, Zap } from 'lucide-react';
import { useTheme } from 'next-themes';
import Link from 'next/link';
import { ReactNode, useEffect, useState } from 'react';
import { Accept } from 'react-dropzone';

export interface FileTypeConfig {
  type: 'pdf' | 'docx' | 'xlsx' | 'pptx' | 'html' | 'epub';
  title: string;
  subtitle: string;
  description: string;
  icon: ReactNode;
  gradientFrom: string;
  gradientTo: string;
  accentColor: string;
  particleColors: string[];
  accept: Accept;
  stats: {
    users: string;
    accuracy: string;
    files: string;
  };
}

interface LandingHeroProps {
  config: FileTypeConfig;
}

export function LandingHero({ config }: LandingHeroProps) {
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setMounted(true), 0);
    return () => window.clearTimeout(timer);
  }, []);

  const isDark = mounted && resolvedTheme === 'dark';

  return (
    <section className="relative overflow-hidden bg-neutral-50 dark:bg-neutral-950 transition-colors duration-500">
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-linear-to-b from-white via-neutral-50 to-neutral-100 dark:from-neutral-950 dark:via-neutral-950 dark:to-neutral-900" />
        <div className="absolute inset-0 opacity-40 dark:opacity-30 bg-[radial-gradient(#94a3b8_1px,transparent_1px)] dark:bg-[radial-gradient(#fff_1px,transparent_1px)] bg-size-[24px_24px]" />
        <div className="absolute inset-x-0 top-0 h-px bg-linear-to-r from-transparent via-neutral-300 to-transparent dark:via-white/20" />
      </div>

      <div className="absolute inset-0 opacity-25 dark:opacity-40 pointer-events-none">
        <Particles
          particleColors={config.particleColors}
          particleCount={100}
          particleSpread={12}
          speed={0.1}
          particleBaseSize={60}
          moveParticlesOnHover={true}
          alphaParticles={true}
          disableRotation={true}
        />
      </div>

      <div className="relative z-10 container mx-auto px-4 py-16 md:py-24">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10 space-y-5">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-3 px-4 py-2 rounded-full border border-neutral-200 bg-white/80 dark:border-white/10 dark:bg-white/5 backdrop-blur-xs"
            >
              <div
                className="w-8 h-8 rounded-xl flex items-center justify-center text-white"
                style={{
                  background: `linear-gradient(135deg, ${config.gradientFrom}, ${config.gradientTo})`,
                }}
              >
                <div className="w-4 h-4">{config.icon}</div>
              </div>
              <span className="text-sm font-medium text-neutral-600 dark:text-neutral-300">
                AI 智能翻译
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-tight"
            >
              <span className="block bg-linear-to-b from-neutral-900 via-neutral-800 to-neutral-500 dark:from-white dark:via-white dark:to-white/60 bg-clip-text text-transparent">
                {config.title.split('·')[0]?.trim()}
              </span>
              {config.title.includes('·') && (
                <span
                  className="block bg-clip-text text-transparent mt-1"
                  style={{
                    backgroundImage: `linear-gradient(135deg, ${config.gradientFrom}, ${config.gradientTo})`,
                  }}
                >
                  {config.title.split('·')[1]?.trim()}
                </span>
              )}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-base md:text-lg text-neutral-600 dark:text-neutral-400 max-w-xl mx-auto"
            >
              {config.subtitle}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="flex flex-wrap items-center justify-center gap-6"
            >
              {[
                {
                  icon: Users,
                  value: config.stats.users,
                  label: '用户',
                  color: config.gradientFrom,
                },
                {
                  icon: CheckCircle,
                  value: config.stats.accuracy,
                  label: '准确率',
                  color: '#10b981',
                },
                {
                  icon: Zap,
                  value: config.stats.files,
                  label: '已处理',
                  color: '#f59e0b',
                },
              ].map((stat, i) => (
                <div key={i} className="flex items-center gap-1.5 text-sm">
                  <stat.icon
                    className="w-4 h-4"
                    style={{ color: stat.color }}
                  />
                  <span className="font-semibold text-neutral-900 dark:text-white">
                    {stat.value}
                  </span>
                  <span className="text-neutral-500">{stat.label}</span>
                </div>
              ))}
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="max-w-4xl mx-auto"
          >
            <div
              className={cn(
                'rounded-2xl border-2 border-dashed',
                'border-neutral-300 dark:border-neutral-700',
                'bg-white/80 dark:bg-white/3',
                'backdrop-blur-xs',
                'hover:border-neutral-400 dark:hover:border-neutral-600',
                'transition-colors duration-300',
              )}
            >
              <UploadFile
                className="border-0 bg-transparent my-0 p-0"
                accept={config.accept}
              >
                <div className="flex flex-col items-center gap-5 py-10 px-6 cursor-pointer group">
                  <div
                    className="p-4 rounded-xl transition-transform duration-300 group-hover:scale-105"
                    style={{
                      background: `linear-gradient(135deg, ${config.gradientFrom}12, ${config.gradientTo}08)`,
                    }}
                  >
                    <Upload
                      className="w-8 h-8"
                      style={{ color: config.gradientFrom }}
                    />
                  </div>

                  <div className="text-center space-y-1">
                    <p className="text-lg font-semibold text-neutral-900 dark:text-white">
                      点击或拖拽上传文件
                    </p>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400">
                      {config.description}
                    </p>
                  </div>

                  <Button
                    size="lg"
                    className="h-11 rounded-full px-6 font-medium text-white"
                    style={{
                      background: `linear-gradient(135deg, ${config.gradientFrom}, ${config.gradientTo})`,
                    }}
                  >
                    选择文件
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </UploadFile>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="flex justify-center mt-6"
          >
            <Link
              href="/pricing"
              className="text-sm text-neutral-500 hover:text-neutral-900 dark:hover:text-white transition-colors flex items-center gap-1"
            >
              查看定价方案 <ArrowRight className="w-3 h-3" />
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
